import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin } from "lucide-react";
import { Reveal, MaskLineInView } from "../components/motion/Reveal";
import { CTAButton } from "../components/ui-regera/CTAButton";
import { useProject } from "../lib/hooks";
import { IMAGES } from "../data/site";

const SECTOR_IMAGE = {
  "Healthcare & Pharma": IMAGES.boardroom,
  "Transport & Logistics": IMAGES.arch4,
  "Energy & Utilities": IMAGES.refinery2,
  "ITES & BPO": IMAGES.dataCenter,
  "Education & Training": IMAGES.meeting,
  "Banking, Financial Services & Insurance": IMAGES.boardroom,
  Manufacturing: IMAGES.refinery,
};

const Meta = ({ label, value }) =>
  value ? (
    <div className="border-b border-white/10 py-4">
      <p className="overline mb-1.5">{label}</p>
      <p className="text-white text-sm lg:text-base">{value}</p>
    </div>
  ) : null;

const Story = ({ n, title, children }) => (
  <div className="border-t border-white/10 pt-8">
    <p className="font-display text-sm font-bold text-[#e60000]">{n}</p>
    <h2 className="mt-2 font-display text-2xl lg:text-3xl font-bold tracking-tight text-white">{title}</h2>
    <div className="mt-4 text-white/80 text-base lg:text-lg leading-relaxed">{children}</div>
  </div>
);

export default function CaseStudy() {
  const { slug } = useParams();
  const { data: p, isLoading, isError } = useProject(slug);

  useEffect(() => {
    if (p) document.title = `${p.title} — REGERA`;
  }, [p]);

  if (isLoading)
    return <div className="pt-40 pb-40 text-center text-white/40">Loading engagement…</div>;
  if (isError || !p)
    return (
      <div className="pt-40 pb-40 text-center">
        <p className="text-white/60 mb-6">Engagement not found.</p>
        <CTAButton to="/portfolio" variant="ghost">BACK TO PORTFOLIO</CTAButton>
      </div>
    );

  const img = SECTOR_IMAGE[p.sector] || IMAGES.arch2;

  return (
    <div data-testid="case-study-page">
      <section className="relative pt-36 lg:pt-48 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={img} alt="" className="h-full w-full object-cover opacity-25 grayscale" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-[#0a0a0a]/70 to-[#0a0a0a]" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <Link
            to="/portfolio"
            data-testid="back-to-portfolio"
            className="inline-flex items-center gap-2 text-xs tracking-[0.16em] text-white/50 hover:text-[#e60000] transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> ALL ENGAGEMENTS
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="bg-[#e60000] text-white text-[10px] tracking-[0.16em] px-3 py-1.5 uppercase">
              {p.type || p.sector}
            </span>
            <span className="inline-flex items-center gap-2 text-xs text-white/50">
              <MapPin className="h-3.5 w-3.5 text-[#e60000]" /> {p.location}
            </span>
          </div>
          <h1 className="font-display font-black tracking-tightest leading-[0.92] text-4xl sm:text-5xl lg:text-7xl text-white max-w-5xl">
            <MaskLineInView>{p.title}</MaskLineInView>
          </h1>
          <Reveal delay={0.15}>
            <p className="mt-6 text-regera-dim text-lg leading-relaxed max-w-2xl">{p.summary}</p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          {/* Structured meta */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <p className="overline mb-4">Engagement profile</p>
              <Meta label="Role & Title" value={p.role} />
              <Meta label="Organisation" value={p.organisation} />
              <Meta label="Engagement" value={p.engagement} />
              <Meta label="Location" value={p.location} />
              <Meta label="Sector" value={p.sector} />
              <Meta label="Type" value={p.type} />
              <Meta label="Resources" value={p.resources} />
              <Meta label="Tenure" value={p.tenure} />
            </div>
          </div>

          {/* Body: the engagement story */}
          <div className="lg:col-span-8 space-y-12">
            <Story n="01" title="Context">
              {[p.organisation, p.sector, p.location].filter(Boolean).join(" · ")}
              {p.engagement ? <><br />{p.engagement}</> : null}
            </Story>
            {p.business_case && <Story n="02" title="Challenge">{p.business_case}</Story>}
            {(p.role || p.activities?.length > 0) && (
              <Story n="03" title="REGERA's contribution">
                {p.role && <span className="block mb-4">Role: {p.role}</span>}
                {p.activities?.length > 0 && (
                  <span className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                    {p.activities.map((a) => (
                      <span key={a} className="flex gap-3 text-base"><span className="mt-2 h-1.5 w-1.5 bg-[#e60000] shrink-0" />{a}</span>
                    ))}
                  </span>
                )}
              </Story>
            )}
            {p.summary && <Story n="04" title="Solution">{p.summary}</Story>}
            {p.outcome && <Story n="05" title="Outcome">{p.outcome}</Story>}

            <div className="border-t border-white/10 pt-10">
              <CTAButton to="/contact#enquiry" testId="case-engage-cta">
                DISCUSS A SIMILAR ENGAGEMENT
              </CTAButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
